import React, { useEffect, useState } from "react";
import { MButton } from "@/components/ui/atoms/MButtons";
import { IconActionCard } from "@/components/icons";
import MCarousel from "@/components/ui/atoms/MCarousel";
import styles from "./ActionCard.module.css";

import { useActionNotifications } from "@/hooks/useActionNotifications";
import { Notification, NotificationType } from "@/types/notification.type";
import { useTranslation } from "react-i18next";
import { useProfileUpdate, getMemberInfo } from "@/hooks/useMembers";
import { useManagedMembersContext } from '@/context/ManagedMembersContext';

interface ActionCardProps {
  styleClass?: string;
  uncategorizedCount?: number;
}

type ActionCardVariant = 'singleAction' | 'multiAction';

interface ActionCardItemProps {
  action: Notification;
  variant: ActionCardVariant;
  handleAccept: (action: Notification) => Promise<void>;
  handleDecline: (action: Notification) => Promise<void>;
  isCarouselMode?: boolean;
}

// Helper function to determine action card variant based on notification type
const getActionVariant = (action: any): ActionCardVariant => {
  const actionType = action.type;
  
  // Single CTA actions
  if (actionType === 'uncategorized_documents' || 
      actionType === 'subscription_ending_soon' || 
      actionType === 'subscription_payment_failed') {
    return 'singleAction';
  }
  
  // Default to multi-action for consent requests and others (including terms_consent)
  return 'multiAction';
};

/**
 * Individual Action Card component with variants.
 */
const ActionCardItem: React.FC<ActionCardItemProps> = ({
  action,
  variant,
  handleAccept,
  handleDecline,
  isCarouselMode = false,
}) => {
  const { t } = useTranslation();

  const onAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    handleAccept(action);
  };

  const onDecline = async (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDecline(action);
  };

  const isUncategorized = (action as any).type === 'uncategorized_documents';
  const isTermsConsent = (action as any).type === 'terms_consent';
  const isSubscriptionEnding = (action as any).type === 'subscription_ending_soon';
  const isSubscriptionPaymentFailed = (action as any).type === 'subscription_payment_failed';

  const getSingleCTAText = () => {
    if (isUncategorized) return "Review documents";
    if (isSubscriptionEnding) return "Renew Subscription";
    if (isSubscriptionPaymentFailed) return "Re-try Payment";
    return t("common.accept");
  };

  const getLeftCTAText = () => {
    if (isTermsConsent) return "View Terms";
    return t("common.decline");
  };

  const getRightCTAText = () => {
    if (isTermsConsent) return "Accept";
    return t("common.accept");
  };

  return (
    <div className={isCarouselMode ? styles.actionCardContainerInner : styles.actionCardContainer}>
      <div className={styles.actionCardTitle}>
        {action.title || "Action Required"}
      </div>
      <div className={styles.actionCardDescription}>
        {action.message}
      </div>
      
      <div className={styles.actionCardCTA}>
        {variant === 'singleAction' ? (
          /* Single CTA */
          <MButton
            variant="filled"
            size="sm"
            onClick={onAccept}
            className={styles.acceptButton}
          >
            {getSingleCTAText()}
          </MButton>
        ) : (
          /* Double CTA - Accept and Decline buttons */
          <>
            <MButton
              variant="filled"
              size="sm"
              onClick={onDecline}
              className={styles.declineButton}
            >
              {getLeftCTAText()}
            </MButton>
            <MButton
              variant="filled"
              size="sm"
              onClick={onAccept}
              className={styles.acceptButton}
            >
              {getRightCTAText()}
            </MButton>
          </>
        )}
      </div>
      
      {/* ActionCard decorative icon at bottom right */}
      <div className={styles.newSvgIcon}>
        <IconActionCard size={162} color="#F6E7C4" />
      </div>
    </div>
  );
};

/**
 * Main ActionCard component that handles single vs multiple actions.
 */
const ActionCard: React.FC<ActionCardProps> = ({ styleClass, uncategorizedCount = 0 }) => {
  const { actions: initialActions = [], handleAccept, handleDecline } =
    useActionNotifications();
  const [actions, setActions] = useState(initialActions);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { updateMemberInLocalStorage } = useProfileUpdate();
  const { fetchManagedMembers } = useManagedMembersContext();

  // Get member info at component level
  const memberInfo = getMemberInfo(true);

  // Check for uncategorized documents and append to actions
  const checkUncategorizedDocuments = () => {
    // Use the uncategorizedCount prop instead of making API call
    if (uncategorizedCount > 0) {
      const uncategorizedAction = {
        id: 'uncategorized-documents',
        title: "Review needed",
        message: "Some documents couldn't be organised correctly and may not appear in search.",
        memberId: memberInfo.id,
        familyId: memberInfo.familyId,
        type: 'uncategorized_documents' as any,
        status: 'pending' as any,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Append uncategorized action only if it doesn't already exist
      // and ensure it's always at the end (after all backend actions)
      setActions(prevActions => {
        const hasUncategorized = prevActions.some(action => action.id === 'uncategorized-documents');
        if (hasUncategorized) {
          return prevActions;
        }
        
        // Filter out any existing uncategorized actions and add the new one at the end
        const backendActions = prevActions.filter(action => action.id !== 'uncategorized-documents');
        return [...backendActions, uncategorizedAction];
      });
    } else {
      // Remove uncategorized action if count is 0
      setActions(prevActions => 
        prevActions.filter(action => action.id !== 'uncategorized-documents')
      );
    }
  };

  // When data changes externally (e.g. refresh)
  useEffect(() => {
    setActions(initialActions);
    setCurrentIndex(0);
    // Check for uncategorized documents when actions update or count changes
    checkUncategorizedDocuments();
  }, [initialActions, uncategorizedCount]);

  const onAccept = async (action: Notification) => {
    try {
      const actionType = (action as any).type;
      
      // Handle action cards (not notifications)
      if (actionType === 'uncategorized_documents') {
        // Navigate to documents landing page
        globalThis.location.href = '/documents';
        return;
      }
      
      if (actionType === 'subscription_ending_soon') {
        // Navigate to subscription page
        globalThis.location.href = '/profile/subscription/manage';
        return;
      }
      
      if (actionType === 'subscription_payment_failed') {
        // Navigate to subscription page for payment retry
        globalThis.location.href = '/profile/subscription/manage';
        return;
      }
      
      if (actionType === 'terms_consent') {
        // Accept terms - mark as read and close
        await handleAccept(action);
        await fetchManagedMembers(true);
        return;
      }
      
      // Handle regular action notifications
      await handleAccept(action); 
      await fetchManagedMembers(true);     
    } catch (error) {
      console.error("Error accepting action:", error);
    }
  };

  const onDecline = async (action: Notification) => {
    try {
      const actionType = (action as any).type;
      
      // Handle terms consent review
      if (actionType === 'terms_consent') {
        // Navigate to terms website
        globalThis.location.href = 'https://maatra.health/terms';
        return;
      }
      
      await handleDecline(action);
      await fetchManagedMembers(true);
      // if the decline is for new fo consent, update the member in local storage
      if (action.type === NotificationType.NEW_FO_CONSENT) {
        updateMemberInLocalStorage(action.memberId);
      }
    } catch (error) {
      console.error("Error declining action:", error);
    }
  };

  if (!actions || actions.length === 0) return null;

  const currentAction = actions[currentIndex];
  const variant: ActionCardVariant = getActionVariant(currentAction);

  // Single action - display directly
  if (actions.length === 1) {
    return (
      <div className={[styles.actionCardWrapper, styleClass].join(" ")} style={!memberInfo.isExperienced ? {width: '100%', position: 'relative'} : {}}>
        <ActionCardItem
          action={currentAction}
          variant={variant}
          handleAccept={onAccept}
          handleDecline={onDecline}
          isCarouselMode={false}
        />
      </div>
    );
  }

  // Multiple actions - carousel inside action card
  return (
    <div className={[styles.actionCardWrapper, styleClass].join(" ")}>
      <div className={styles.actionCardContainer} style={!memberInfo.isExperienced ? {width: '100%', position: 'relative'} : {}}>
        <div className={styles.actionCardContainerInner}>
          <MCarousel
            currentIndex={currentIndex}
            onIndexChange={setCurrentIndex}
            variant="actionCard"
          >
            {actions.map((action) => {
              const actionVariant = getActionVariant(action);
              
              return (
                <ActionCardItem
                  key={action.id}
                  action={action}
                  variant={actionVariant}
                  handleAccept={onAccept}
                  handleDecline={onDecline}
                  isCarouselMode={true}
                />
              );
            })}
          </MCarousel>
        </div>
      </div>
    </div>
  );
};

export default ActionCard;
